import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAccessToken } from '@features/user';
import {
  AnyModule,
  CreateModulePayload,
  ExtractionPrompts,
  MODEL_NAMES,
  PublicModule,
  UpdateModulePayload,
  UserModuleAdmin,
} from './module.types';
import { createModule, updateModule } from './module.api';

interface ModuleFormProps {
  module?: AnyModule;
  onCancel: () => void;
  onSave: () => void;
  isCreating?: boolean;
}

export const ModuleForm: React.FC<ModuleFormProps> = ({
  module,
  onCancel,
  onSave,
  isCreating = false,
}) => {
  const navigate = useNavigate();
  const { getAccessToken } = useAccessToken();
  const [loading, setLoading] = useState(false);
  const [saveAndCreateAnother, setSaveAndCreateAnother] = useState(false);

  // Form state
  const [moduleType, setModuleType] = useState<'public' | 'user'>(
    module ? module._moduleType : 'public',
  );

  const [moduleName, setModuleName] = useState(module?.module_name || '');
  const [generationPrompt, setGenerationPrompt] = useState(module?.generation_prompt || '');
  const [semanticPrompt, setSemanticPrompt] = useState(
    module?.extraction_prompts?.semantic_prompt || '',
  );
  const [keywordPrompt, setKeywordPrompt] = useState(
    module?.extraction_prompts?.keyword_prompt || '',
  );
  const [updatePrompt, setUpdatePrompt] = useState(
    module && module._moduleType === 'public' ? module.update_prompt || '' : '',
  );
  const [placeholders, setPlaceholders] = useState<string[]>(
    module && module._moduleType === 'public' && module.placeholders
      ? [...module.placeholders]
      : [],
  );
  const [selectedModels, setSelectedModels] = useState<string[]>(
    module?.models ? [...module.models] : ['gpt-4o'],
  );
  const [temperature, setTemperature] = useState<number>(module?.temperature || 0.7);
  const [maxTokens, setMaxTokens] = useState<number | string>(module?.max_tokens || 2500);
  const [isPublic, setIsPublic] = useState<boolean>(
    module && module._moduleType === 'public' ? !!module.public : false,
  );
  const [publicName, setPublicName] = useState(
    module && module._moduleType === 'public' ? module.public_name || '' : '',
  );
  const [publicDescription, setPublicDescription] = useState(
    module && module._moduleType === 'public' ? module.public_description || '' : '',
  );
  const [publicCategories, setPublicCategories] = useState<string[]>(
    module && module._moduleType === 'public' && module.public_categories
      ? [...module.public_categories]
      : [],
  );
  const [publicExample, setPublicExample] = useState(
    module && module._moduleType === 'public' ? module.public_example || '' : '',
  );
  const [isMetaModule, setIsMetaModule] = useState<boolean>(module?.meta_module || false);
  const [isMarkdown, setIsMarkdown] = useState<boolean>(module?.markdown || true);

  // User module specific state
  const [language, setLanguage] = useState((module as UserModuleAdmin)?.language || 'en');
  const [userModuleDescription, setUserModuleDescription] = useState(
    (module as UserModuleAdmin)?.user_module_description || '',
  );
  const [userExample, setUserExample] = useState((module as UserModuleAdmin)?.user_example || '');
  const [isDeactivated, setIsDeactivated] = useState<boolean>(
    (module as UserModuleAdmin)?.deactivated || false,
  );

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate required fields
    const validationErrors: Record<string, string> = {};

    if (!moduleName.trim()) {
      validationErrors.moduleName = 'Module name is required';
    }

    if (!generationPrompt.trim()) {
      validationErrors.generationPrompt = 'Generation prompt is required';
    }

    setErrors(validationErrors);

    // If there are validation errors, don't proceed
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      // Get access token
      const accessToken = await getAccessToken();
      if (!accessToken) {
        console.error('No access token available');
        setLoading(false);
        return;
      }

      // Prepare extraction prompts
      const extractionPrompts: ExtractionPrompts = {
        semantic_prompt: semanticPrompt.trim(),
        keyword_prompt: keywordPrompt.trim(),
      };

      if (isCreating) {
        // Create new module payload
        const createPayload: CreateModulePayload = {
          _moduleType: moduleType,
          module_name: moduleName.trim(),
          generation_prompt: generationPrompt.trim(),
          extraction_prompts: extractionPrompts,
          update_prompt: updatePrompt.trim() || undefined,
          models: selectedModels,
          temperature,
          max_tokens: typeof maxTokens === 'string' ? parseInt(maxTokens, 10) : maxTokens,
          meta_module: isMetaModule,
          markdown: isMarkdown,
        };

        // Add type-specific fields
        if (moduleType === 'public') {
          createPayload.public = isPublic;
          createPayload.public_name = publicName.trim() || undefined;
          createPayload.public_description = publicDescription.trim() || undefined;
          createPayload.public_categories =
            publicCategories.length > 0 ? publicCategories : undefined;
          createPayload.public_example = publicExample.trim() || undefined;
          createPayload.placeholders = placeholders.length > 0 ? placeholders : undefined;
        } else {
          createPayload.language = language;
          createPayload.user_module_description = userModuleDescription.trim() || undefined;
          createPayload.user_example = userExample.trim() || undefined;
        }

        await createModule(createPayload, accessToken);
      } else if (module) {
        // Update existing module payload
        const updatePayload: UpdateModulePayload = {
          _moduleType: moduleType,
          module_name: moduleName.trim(),
          generation_prompt: generationPrompt.trim(),
          extraction_prompts: extractionPrompts,
          update_prompt: updatePrompt.trim() || undefined,
          models: selectedModels,
          temperature,
          max_tokens: typeof maxTokens === 'string' ? parseInt(maxTokens, 10) : maxTokens,
          meta_module: isMetaModule,
          markdown: isMarkdown,
        };

        // Add type-specific fields
        if (moduleType === 'public') {
          updatePayload.public = isPublic;
          updatePayload.public_name = publicName.trim() || undefined;
          updatePayload.public_description = publicDescription.trim() || undefined;
          updatePayload.public_categories =
            publicCategories.length > 0 ? publicCategories : undefined;
          updatePayload.public_example = publicExample.trim() || undefined;
          updatePayload.placeholders = placeholders.length > 0 ? placeholders : undefined;
        } else {
          updatePayload.language = language;
          updatePayload.user_module_description = userModuleDescription.trim() || undefined;
          updatePayload.user_example = userExample.trim() || undefined;
          updatePayload.deactivated = isDeactivated;
        }

        await updateModule(
          module.module_id,
          module.module_version,
          moduleType,
          updatePayload,
          accessToken,
        );
      }

      // Navigate back to the list view or create another
      if (saveAndCreateAnother) {
        // Reset form for creating another
        setModuleName('');
        setGenerationPrompt('');
        setSemanticPrompt('');
        setKeywordPrompt('');
        setUpdatePrompt('');
        setPlaceholders([]);
        setPublicName('');
        setPublicDescription('');
        setPublicCategories([]);
        setPublicExample('');
        setUserModuleDescription('');
        setUserExample('');
        setSaveAndCreateAnother(false);
      } else {
        onSave();
      }
    } catch (error) {
      console.error('Failed to save module:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle model selection change
  const handleModelsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedModels(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle placeholder changes
  const handlePlaceholderAdd = (value: string) => {
    if (value && !placeholders.includes(value)) {
      setPlaceholders([...placeholders, value]);
    }
  };

  const handlePlaceholderDelete = (placeholder: string) => {
    setPlaceholders(placeholders.filter(p => p !== placeholder));
  };

  // Handle public categories changes
  const handleCategoryAdd = (value: string) => {
    if (value && !publicCategories.includes(value)) {
      setPublicCategories([...publicCategories, value]);
    }
  };

  const handleCategoryDelete = (category: string) => {
    setPublicCategories(publicCategories.filter(c => c !== category));
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2">
            {isCreating ? 'Create New Module' : 'Edit Module'}
          </Typography>
          {module && (
            <Typography variant="subtitle2" color="text.secondary">
              {module.module_id}:{module.module_version}
            </Typography>
          )}
        </Box>

        {/* Module Type Selection (only when creating) */}
        {isCreating && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="module-type-label">Module Type</InputLabel>
              <Select
                labelId="module-type-label"
                id="module-type"
                value={moduleType}
                label="Module Type"
                onChange={e => setModuleType(e.target.value as 'public' | 'user')}
              >
                <MenuItem value="public">Public Module</MenuItem>
                <MenuItem value="user">User Module</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Basic Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Basic Information
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                id="module-name"
                label="Module Name"
                value={moduleName}
                onChange={e => setModuleName(e.target.value)}
                error={!!errors.moduleName}
                helperText={errors.moduleName}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                id="generation-prompt"
                label="Generation Prompt"
                multiline
                rows={6}
                value={generationPrompt}
                onChange={e => setGenerationPrompt(e.target.value)}
                error={!!errors.generationPrompt}
                helperText={errors.generationPrompt}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="semantic-prompt"
                label="Extraction: Semantic Prompt (;-delimited)"
                multiline
                rows={3}
                value={semanticPrompt}
                onChange={e => setSemanticPrompt(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="keyword-prompt"
                label="Extraction: Keyword Prompt (;-delimited)"
                multiline
                rows={3}
                value={keywordPrompt}
                onChange={e => setKeywordPrompt(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Model Configuration */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Model Configuration
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel id="models-label">Models</InputLabel>
                <Select
                  labelId="models-label"
                  id="models"
                  multiple
                  value={selectedModels}
                  onChange={handleModelsChange}
                  input={<OutlinedInput label="Models" />}
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(value => (
                        <Chip key={value} label={MODEL_NAMES[value] || value} />
                      ))}
                    </Box>
                  )}
                >
                  {Object.entries(MODEL_NAMES).map(([code, name]) => (
                    <MenuItem key={code} value={code}>
                      <Checkbox checked={selectedModels.indexOf(code) > -1} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography gutterBottom>Temperature: {temperature}</Typography>
              <Slider
                value={temperature}
                onChange={(_, newValue) => setTemperature(newValue as number)}
                step={0.1}
                marks
                min={0}
                max={1}
                valueLabelDisplay="auto"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="max-tokens"
                label="Max Tokens"
                type="number"
                value={maxTokens}
                onChange={e => setMaxTokens(e.target.value)}
               slotProps={{ input: { inputProps: { min: 0 } } }}/>
            </Grid>
          </Grid>
        </Box>

        {/* Module Settings */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Module Settings
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isMetaModule}
                    onChange={e => setIsMetaModule(e.target.checked)}
                  />
                }
                label="Meta Module"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch checked={isMarkdown} onChange={e => setIsMarkdown(e.target.checked)} />
                }
                label="Markdown"
              />
            </Grid>

            {/* Public Module specific settings */}
            {moduleType === 'public' && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
                    }
                    label="Public"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="public-name"
                    label="Public Display Name"
                    value={publicName}
                    onChange={e => setPublicName(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="public-description"
                    label="Public Description"
                    multiline
                    rows={2}
                    value={publicDescription}
                    onChange={e => setPublicDescription(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="public-example"
                    label="Public Example"
                    multiline
                    rows={3}
                    value={publicExample}
                    onChange={e => setPublicExample(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="categories-input"
                    label="Public Categories"
                    helperText="Press Enter to add"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const inputElement = e.target as HTMLInputElement;
                        handleCategoryAdd(inputElement.value);
                        inputElement.value = '';
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {publicCategories.map(category => (
                      <Chip
                        key={category}
                        label={category}
                        onDelete={() => handleCategoryDelete(category)}
                      />
                    ))}
                  </Box>
                </Grid>
              </>
            )}

            {/* User Module specific settings */}
            {moduleType === 'user' && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="language-label">Language</InputLabel>
                    <Select
                      labelId="language-label"
                      id="language"
                      value={language}
                      label="Language"
                      onChange={e => setLanguage(e.target.value)}
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                      <MenuItem value="de">German</MenuItem>
                      {/* Add more languages as needed */}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  {!isCreating && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isDeactivated}
                          onChange={e => setIsDeactivated(e.target.checked)}
                        />
                      }
                      label="Deactivated"
                    />
                  )}
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="user-module-description"
                    label="User Module Description"
                    multiline
                    rows={2}
                    value={userModuleDescription}
                    onChange={e => setUserModuleDescription(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="user-example"
                    label="User Example"
                    multiline
                    rows={3}
                    value={userExample}
                    onChange={e => setUserExample(e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Form Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>

          {isCreating && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setSaveAndCreateAnother(true);
                handleSubmit(new Event('submit') as any);
              }}
            >
              Save & Create Another
            </Button>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setSaveAndCreateAnother(false);
              handleSubmit(e);
            }}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {isCreating ? 'Create' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ModuleForm;
